import { useState } from 'react';
import { useBusiness } from 'hooks/useBusiness';
import { useLocalization } from 'hooks/useLocalization';
import { Button, Box, Alert, Paper, Collapse, FormGroup, FormControlLabel, Checkbox, FormControl, FormLabel, RadioGroup, Radio } from '@mui/material'
import {
	KeyboardDoubleArrowLeft as KeyboardDoubleArrowLeftIcon,
	KeyboardDoubleArrowRight as KeyboardDoubleArrowRightIcon,
	Checklist as ChecklistIcon,
	PictureAsPdf
} from '@mui/icons-material';
import { formatNumberWithSpacesAndRounding } from 'utils/formatUtils';
import 'components/booking/style.css';
import LoadingButton from '@mui/lab/LoadingButton';
import { Booking } from 'interfaces/BookingInterfaces';

interface PaymentMethodButtonProps {
	method: any;
	expandedPaymentId: number | null;
	setExpandedPaymentId: (id: number | null) => void;
	booking: Booking;
	terms: any;
	selectedDepositOption: number;
	setSelectedDepositOption: (option: number) => void;
	setIsTermsOpen: (open: boolean) => void;
	isTermsAccepted: boolean;
	onTermsAccepted: (accepted: boolean) => void;
	isRedirecting: boolean;
	setIsRedirecting: (redirecting: boolean) => void;
	setIsErrorDialogOpen: (open: boolean) => void;
	setErrorDialogContents: (contents: string | null) => void;
}

function PaymentMethodButton({ method, expandedPaymentId, setExpandedPaymentId, booking, terms, selectedDepositOption, setSelectedDepositOption, setIsTermsOpen, isTermsAccepted, onTermsAccepted, isRedirecting, setIsRedirecting, setIsErrorDialogOpen, setErrorDialogContents }: PaymentMethodButtonProps) {
	const { getStr, langCode } = useLocalization();
	const { getExchangeFactor, sendRequest, getBizConfigVar } = useBusiness();
	const isExpanded = expandedPaymentId === method.code;
	const isVisible = expandedPaymentId === null || expandedPaymentId === method.code;
	const leftToPay = booking.total - booking.totalPaid;
	const bookingTotalInclSurcharge = method.surcharge > 0 ? leftToPay + (leftToPay * method.surcharge / 100) : leftToPay;
	const hasTerms = terms.length > 0;

	const depositOptionToUse = booking.status === 'D' ? 100 : selectedDepositOption; //If booking is already deposited, the rest must be paid

	const getExchangedTotal = (amtToPayNowBookingCurrency: number) => {
		return amtToPayNowBookingCurrency / getExchangeFactor(method.currencyCode, booking.currencyCode);
	};

	const setupRedirect = (paymentMethodCode: string) => {
		setIsRedirecting(true);

		sendRequest({
			type: 'setupGatewayRedirect',
			params: {
				gatewayCode: paymentMethodCode,
				payItemType: 'booking',
				payItemId: booking.bookingNumber,
				selectedDepositOption: depositOptionToUse,
				langCode: langCode,
				embedded: true,
				userAgent: navigator.userAgent,
				appRootUrl: getBizConfigVar('appRootUrlToUse')
			},
			onSuccess: (data) => {
				setIsRedirecting(false); //Remove loading animation

				if (data.setupGatewayRedirect.redirectUrl) {
					window.location.href = data.setupGatewayRedirect.redirectUrl;
				} else { //No redirect URL					
					if (data.setupGatewayRedirect.error) {
						setErrorDialogContents(data.setupGatewayRedirect.error);
						setIsErrorDialogOpen(true);
					} else { //Unexpected error
						setErrorDialogContents(getStr('Unexpected_error'));
						setIsErrorDialogOpen(true);	
					}
				}
			},
			onError: (errorMessage) => {
				setIsRedirecting(false);
				setErrorDialogContents(errorMessage);
				setIsErrorDialogOpen(true);
			}
		});
	};

	const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedDepositOption(parseInt(event.target.value));
	};

	const amtToPayNowBookingCurrency = bookingTotalInclSurcharge * depositOptionToUse / 100;

	return (
		<>
			<Collapse in={isVisible}>
				<Collapse in={isExpanded}>
					<Button
						variant="outlined"
						onClick={() => {setExpandedPaymentId(null)}}
						startIcon={<KeyboardDoubleArrowLeftIcon />}
						sx={{ mb: 2 }}
					>
						{getStr('Back')}
					</Button>
				</Collapse>
				<Paper className={`payment-method-button ${expandedPaymentId === method.code ? 'payment-method-button-expanded' : 'payment-method-button-not-expanded'}`} onClick={() => {expandedPaymentId === null && setExpandedPaymentId(method.code)}}>
					<Box className="payment-method-button-content">
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<Box sx={{ width: '200px' }}>
								<img src={`https://tourtask.com/images/misc/gateways/logo-${method.code}-200x60.png`} alt={method.code} style={{ width: '200px', height: '60px' }} />
							</Box>
							<Box sx={{ flexGrow: 1, textAlign: 'right', fontSize: '0.8em' }}>
								{method.surcharge > 0 && (
									<div style={{ marginBottom: '5px' }}>
										+{ method.surcharge }% {getStr('fee')}
									</div>
								)}
								<div>
									{
										method.optionIcons.map((iconStr: string) => (
											<img key={iconStr} src={`https://tourtask.com/images/misc/payment-options/${iconStr}.png`} alt={iconStr} style={{ width: '24px', height: '15px', marginRight: '3px' }} />
										))
									}
								</div>
							</Box>
						</Box>
						<Collapse in={isExpanded}>
							{method.surcharge > 0 && (
								<Alert severity="info">{getStr('GATEWAY_EXTRA_TRANSACTION_FEE_INFO')}</Alert>
							)}
							<table style={{ width: '100%', paddingTop: '20px', marginBottom: '15px' }}>
								<tbody>
									<tr>
										<td>
											{getStr('Per_person')}
										</td>
										<td style={{ textAlign: 'right' }}>
											{formatNumberWithSpacesAndRounding(bookingTotalInclSurcharge / booking.paxQty)} {booking.currencyCode}
										</td>
									</tr>
									<tr>
										<td>
											{getStr('Total')}
										</td>
										<td style={{ textAlign: 'right' }}>
											{formatNumberWithSpacesAndRounding(bookingTotalInclSurcharge)} {booking.currencyCode}
										</td>
									</tr>
								</tbody>
							</table>

							{booking.status !== 'D' && (booking.depositOptions.length > 1 || booking.depositOptions[0] !== 100) ? (
								<Box sx={{ margin: '30px 0' }}>
									<FormControl>
										<FormLabel id="deposit-options-group-label">{getStr('DEPOSIT_How_much_pay_now_Q')}</FormLabel>
										<RadioGroup
											aria-labelledby="deposit-options-group-label"
											value={depositOptionToUse}
											name="radio-buttons-group"
										>
											{booking.depositOptions.map((option: number) => (
												<FormControlLabel
													key={option}
													value={option}
													control={<Radio onChange={handleOptionChange}/>}
													label={`${option}%`}
												/>
											))}
										</RadioGroup>
									</FormControl>
								</Box>
							) : (
								null
							)}

							{hasTerms && (
								<>
									<Button
										variant="outlined"
										onClick={() => setIsTermsOpen(true)}
										startIcon={<ChecklistIcon />}
									>
										{getStr('Read_terms')}
									</Button>
									<FormGroup sx={{ mb: '15px' }}>
										<FormControlLabel
											control={
												<Checkbox
													checked={isTermsAccepted}
													onChange={(e) => onTermsAccepted(e.target.checked)}
												/>
											}
											label={getStr('I_have_read_and_accept_the_terms')}
										/>
									</FormGroup>
								</>
							)}
							<LoadingButton
								loading={isRedirecting}
								size="large"
								variant="contained"
								sx={{ p: 2, mt: 3 }}
								endIcon={<KeyboardDoubleArrowRightIcon />}
								disabled={(hasTerms && !isTermsAccepted) || isRedirecting}
								onClick={() => {setupRedirect(method.code)}}
								fullWidth
							>
								<Box display="flex" flexDirection="column" alignItems="center">
									<Box sx={{ fontSize: '1.5em', lineHeight: '1.5em', fontWeight: 'bold' }}>
										{getStr('Pay_now')}
									</Box>
									<Box sx={{ fontSize: '1.1em', lineHeight: '1.1em' }}>
										{formatNumberWithSpacesAndRounding(amtToPayNowBookingCurrency)} {booking.currencyCode}
									</Box>
									{booking.currencyCode !== method.currencyCode && (
										<Box sx={{ fontSize: '0.8em' }}>
											{getStr('PAY_IN_DIFF_CURR_Will_be_charged_as')} <b>{formatNumberWithSpacesAndRounding(getExchangedTotal(amtToPayNowBookingCurrency))} {method.currencyCode}</b>
										</Box>
									)}
								</Box>
							</LoadingButton>
						</Collapse>
					</Box>
					<Collapse in={!isExpanded}>
						<Box className="payment-method-button-desc">
							{method.descPublic}
						</Box>
					</Collapse>
				</Paper>
			</Collapse>
		</>
	);
}

export default PaymentMethodButton;